import { useSelector } from 'react-redux';
import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { axFetch } from '/@/store/session/actions';
import { getUser } from '/@/store/session/selectors';
import type { User } from '/@/types/core';
import {
    isUserValidatedAdvisor,
    isUserValidatedConsultant,
    isUserValidatedOther,
    isUserValidatedRV,
    isUserValidatedSquad,
    isValidatedBayerUserType,
} from '/@/utils';
import { PROFILES, type ProfileCapabilities, type UserType } from './profiles';

export type { NavbarTabKey, ProfileCapabilities, UserType } from './profiles';
export { PROFILES, USER_TYPES } from './profiles';

export const getLocationsFromApi = async ({
    level,
    page,
    pageSize,
    parentIdIn,
    parentId,
}: {
    level: number;
    page?: number;
    pageSize?: number;
    parentIdIn?: number[];
    parentId?: number;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/zones/locations/`, window.location.origin);
    const res = await axFetch.get(apiUrl.toString(), {
        params: {
            page_size: pageSize,
            page,
            level,
            parent_id__in: parentIdIn?.join(','),
            parent_id: parentId,
        },
    });
    return res.data;
};

export const useLocations = (
    {
        level = 0,
        page,
        pageSize = 10,
        parentIdIn,
        parentId,
    }: {
        level: number;
        page?: number;
        pageSize?: number;
        parentIdIn?: number[];
        parentId?: number;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) =>
    useSWRImmutable(
        enabled ? { key: 'getLocationsFromApi', page, pageSize, level, parentIdIn, parentId } : null,
        getLocationsFromApi
    );

export const useIsValidatedRV = () => {
    const user = useSelector(getUser);
    return isUserValidatedRV(user as User);
};

export const useIsValidatedAdvisor = () => {
    const user = useSelector(getUser);
    return isUserValidatedAdvisor(user as User);
};
export const useIsValidatedSquad = () => {
    const user = useSelector(getUser);
    return isUserValidatedSquad(user as User);
};

export const useIsValidatedOther = () => {
    const user = useSelector(getUser);
    return isUserValidatedOther(user as User);
};

export const useValidatedType = (): UserType => {
    const isValidatedRV = useIsValidatedRV();
    const isValidatedConsultant = useIsValidatedConsultant();
    const isValidatedSquad = useIsValidatedSquad();
    const isValidatedAdvisor = useIsValidatedAdvisor();
    const isValidatedOther = useIsValidatedOther();

    if (isValidatedSquad) {
        return 'squad_coach';
    }
    if (isValidatedRV) {
        return 'rv';
    }
    if (isValidatedConsultant) {
        return 'consultant';
    }
    if (isValidatedAdvisor) {
        return 'advisor';
    }
    if (isValidatedOther) {
        return 'other';
    }
    return 'grower';
};

/** Prefer this over adding a new `isValidatedX` check at a call site. */
export const useCapabilities = (): ProfileCapabilities => PROFILES[useValidatedType()];

export const useIsValidatedConsultant = () => {
    const user = useSelector(getUser);
    return isUserValidatedConsultant(user as User);
};

export const useHasWriteAccess = () => {
    const isUserValidatedSquad = useIsValidatedSquad();
    return !isUserValidatedSquad;
};

export const useIsValidatedBayerUserType = () => {
    const user = useSelector(getUser);
    return isValidatedBayerUserType(user as User);
};
